import React, { createContext, useContext, useState, useEffect } from 'react';
import { initClarity } from '@/libs/clarity';

export interface AppConfig {
  'clarity:project_id'?: string;
  [key: string]: any;
}

interface AppContextType {
  config: AppConfig;
  isLoading: boolean;
  error: Error | null;
  refreshConfig: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const configUrl = `${apiBase.replace(/\/$/, '')}/app/config`;
      const response = await fetch(configUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch app config: ${response.statusText}`);
      }
      const data: AppConfig = await response.json();
      setConfig(data);
      setError(null);

      // Trigger Clarity if clarity:project_id is present
      const clarityProjectId = data['clarity:project_id'];
      if (clarityProjectId) {
        initClarity(clarityProjectId);
      }
    } catch (err: any) {
      console.error('Error loading app config in AppContext:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return (
    <AppContext.Provider
      value={{
        config,
        isLoading,
        error,
        refreshConfig: fetchConfig,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppConfig = () => {
  const { config, isLoading, error, refreshConfig } = useApp();
  return { config, isLoading, error, refreshConfig };
};
